import { assign, createMachine } from '@monofront/xstate-v4';
import { logger } from '@monofront/logging';
import { CHECKOUT_COM_CARD_FORM_STATES } from './states';
import { CHECKOUTCOM_CARD_FORM_TRANSITIONS } from './transitions';
import {
  ICheckoutComContext,
  ICheckoutComEvents,
  IFlowPaymentCompletedEvent,
  IOpen3DSecureEvent,
} from './types';

export const CHECKOUTCOM_CARD_FORM_MACHINE_ID = 'checkoutcom-card-form';

const open3DSecureModal = assign<ICheckoutComContext, IOpen3DSecureEvent>({
  is3DSecureOpen: (_, _event) => true,
  url3DSecure: (_, event) => event.url3DSecure,
  paymentId: (_, event) => event?.paymentId || null,
  saveCardForFutureUse: (_, event) => event?.saveCardForFutureUse || false,
});

const close3DSecureModal = assign<ICheckoutComContext>({
  is3DSecureOpen: () => false,
});

const setFlowPaymentId = assign<
  ICheckoutComContext,
  IFlowPaymentCompletedEvent
>({
  flowPaymentId: (_, event) => event.paymentId,
});

const logFlowCompletedFromInitial = (
  _: ICheckoutComContext,
  event: IFlowPaymentCompletedEvent,
) =>
  logger.error(
    'Flow payment completed while the card form machine was back in INITIAL',
    { payment_id: event.paymentId },
  );

const initialContext: ICheckoutComContext = {
  is3DSecureOpen: false,
  url3DSecure: '',
  paymentId: null,
  saveCardForFutureUse: false,
  flowPaymentId: null,
};

export const checkoutcomCardFormMachine = createMachine<ICheckoutComContext>({
  id: CHECKOUTCOM_CARD_FORM_MACHINE_ID,
  initial: CHECKOUT_COM_CARD_FORM_STATES.INITIAL,
  context: initialContext,
  states: {
    [CHECKOUT_COM_CARD_FORM_STATES.INITIAL]: {
      entry: assign({
        ...initialContext,
      }),
      on: {
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.SUBMIT_CARD]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.CARD_TOKENIZED],
        },
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.FLOW_MOUNTED]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.FLOW_READY],
        },
        // Defence in depth. Nothing on the Flow path sends RESET any more, so the
        // machine should never sit here while the widget is still mounted, and
        // FLOW_MOUNTED fires once per session so it could not get back to
        // FLOW_READY on its own. Should that regress, a payment Checkout.com has
        // already taken must still reach the redirect instead of being dropped.
        // The log is the alarm: it means the guarantee above no longer holds.
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.FLOW_PAYMENT_COMPLETED]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.FLOW_PAYMENT_COMPLETED],
          actions: [logFlowCompletedFromInitial, setFlowPaymentId],
        },
      },
    },
    [CHECKOUT_COM_CARD_FORM_STATES.CARD_TOKENIZED]: {
      entry: ['notifyCardTokenized'],
      on: {
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.CREATE_PAYMENT_WITH_3DS]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_OPEN']],
          actions: open3DSecureModal,
        },
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.INITIAL],
        },
      },
    },
    [CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_OPEN']]: {
      on: {
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.INITIAL],
        },
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.VALIDATE_3DS]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_SUCCEEDED']],
        },
      },
      entry: ['create3dsToken'],
      exit: ['remove3dsToken'],
    },
    [CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_SUCCEEDED']]: {
      on: {
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.COMPLETE_ORDER]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.COMPLETING_ORDER],
        },
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.INITIAL],
        },
      },
    },
    // The order is submitted by an invoked service, which XState runs exactly
    // once per state entry. COMPLETE_ORDER is only handled in
    // 3D_SECURE_SUCCEEDED, so a CTA that re-sends it on re-render is a no-op
    // and cannot submit the order a second time.
    [CHECKOUT_COM_CARD_FORM_STATES.COMPLETING_ORDER]: {
      // 3DS has already succeeded, so the iframe has served its purpose. Close it
      // on entry rather than waiting for INITIAL's reset, which a successful
      // completion never reaches: otherwise a slow or lost post-completion
      // navigation (mobile webview drives it from sendMessageToMobile) leaves the
      // customer behind a 3DS modal for an order that is already placed.
      entry: close3DSecureModal,
      invoke: {
        id: 'completeOrder',
        src: 'completeOrder',
        onDone: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.ORDER_COMPLETED],
        },
        onError: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.INITIAL],
        },
      },
    },
    // Flow-based states
    [CHECKOUT_COM_CARD_FORM_STATES.FLOW_READY]: {
      on: {
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.FLOW_PAYMENT_COMPLETED]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.FLOW_PAYMENT_COMPLETED],
          actions: setFlowPaymentId,
        },
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.INITIAL],
        },
      },
    },
    [CHECKOUT_COM_CARD_FORM_STATES.FLOW_PAYMENT_COMPLETED]: {
      on: {
        [CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET]: {
          target: [CHECKOUT_COM_CARD_FORM_STATES.INITIAL],
        },
      },
    },
    // Terminal by omission: it handles no events, so a second submission stays
    // unreachable for the lifetime of the machine. Deliberately not `type: 'final'`
    // — XState stops the interpreter once the machine reaches a done state, which
    // silently drops every later event, including the RESET the 3DS modal's close
    // button sends.
    [CHECKOUT_COM_CARD_FORM_STATES.ORDER_COMPLETED]: {},
  },
  schema: {
    events: {} as ICheckoutComEvents,
  },
  predictableActionArguments: true,
  preserveActionOrder: true,
});
